/**
 * shared/draft.ts —— 把 AI 起草的双周报切成四段（2026-09-23 新增）
 * 出处：01_需求文档/15_AI接入方案.md §五
 *
 * 四个段落标题写死在后端 system prompt 里（`modules/assistant/service.ts`），
 * 与周报编辑页的四个输入框一一对应。切开后每段各带一个「复制本段」，
 * 用户不用再从一整块里手动拆。
 *
 * ⚠ 这是**纯展示**：不写库、不跨页传草稿（红线 3 / `15` §五）。
 * ⚠ 四个标题**必须全部出现且顺序正确**才切；缺一个就返回 null，整条回答按普通回答显示——
 *   切错段比不切更糟（用户会把「下期计划」粘进「本期进展」）。
 * ⚠ 模型偶尔会给标题套 `**…**`、`#`、冒号，这里把同一行上的这些符号一起吃掉。
 */

/** 与周报编辑页的四个字段同名同序（`pages/reports.ts`）。⚠ 与后端 system prompt 的标题逐字一致 */
export const DRAFT_TITLES = ['本期进展与成果', '下期计划', '问题与风险', '资源诉求'] as const;

export interface DraftPart {
  title: (typeof DRAFT_TITLES)[number];
  body: string;
}

export interface DraftSplit {
  /** 第一个标题之前的说明（可能为空） */
  head: string;
  parts: DraftPart[];
  /** 服务端追加的裁剪提示（「（本次回答未纳入…）」），单独放，不混进「资源诉求」被复制走 */
  tail: string;
}

const LEAD = ' \t*#>-';
const TRAIL = ' \t*:：';

export function splitDraft(text: string): DraftSplit | null {
  const spans: { start: number; end: number }[] = [];
  let from = 0;
  for (const t of DRAFT_TITLES) {
    const mark = `【${t}】`;
    const i = text.indexOf(mark, from);
    if (i < 0) return null;
    if (text.indexOf(mark, i + mark.length) >= 0) return null; // 同一标题出现两次，不知道该切哪
    let start = i;
    while (start > 0 && LEAD.includes(text[start - 1] ?? '\n')) start--;
    let end = i + mark.length;
    while (end < text.length && TRAIL.includes(text[end] ?? '\n')) end++;
    spans.push({ start, end });
    from = end;
  }

  const bodies = spans.map((s, k) => text.slice(s.end, spans[k + 1]?.start ?? text.length));
  /* 最后一段末尾可能挂着服务端追加的裁剪提示 */
  let tail = '';
  const last = bodies.length - 1;
  const m = /\n+(（本次回答[^\n]*）)\s*$/.exec(bodies[last] ?? '');
  if (m) {
    tail = m[1] ?? '';
    bodies[last] = (bodies[last] ?? '').slice(0, m.index);
  }

  return {
    head: text.slice(0, spans[0]?.start ?? 0).trim(),
    parts: DRAFT_TITLES.map((title, k) => ({ title, body: (bodies[k] ?? '').trim() })),
    tail,
  };
}
